/**
 * Web Share API helpers — used to share PDFs (proforma, invoice,
 * release-note) directly to WhatsApp / email / AirDrop / etc. via the
 * native OS share sheet.
 *
 * Browser support summary (May 2026):
 *   - iOS Safari 15+   — full file share support
 *   - Android Chrome   — full file share support
 *   - Desktop browsers — Chrome supports text/url, file share is
 *                        spotty; Firefox/Safari desktop don't expose
 *                        navigator.share at all
 *
 * The exported helpers therefore feature-check before doing anything
 * destructive, and the page-level UI uses `canShareFiles()` (sync) to
 * decide whether to render the share button at all. There's no point
 * showing a button that does nothing.
 *
 * Usage:
 *
 *   import { canShareFiles, sharePdfBlob } from '../utils/share';
 *
 *   if (canShareFiles()) {
 *     // render Share button
 *   }
 *
 *   async function onShare() {
 *     const blob = await fetchPdfBlob();
 *     const result = await sharePdfBlob(blob, 'PRO-2026-0042.pdf', {
 *       title: 'Proforma PRO-2026-0042',
 *       text:  'Proforma invoice for ACME Co.',
 *     });
 *     if (result.outcome === 'shared')   // success
 *     if (result.outcome === 'cancelled') // user dismissed share sheet
 *     if (result.outcome === 'unsupported') // fall back to view-in-tab
 *     if (result.outcome === 'error') console.error(result.error);
 *   }
 */

/**
 * Sync feature check — true iff the browser exposes `navigator.share`
 * AND `navigator.canShare` AND the device claims it can share a sample
 * PDF file. We probe with a 1-byte zero-content file rather than the
 * actual PDF (which we may not have fetched yet) — same MIME, same
 * extension, so the canShare contract holds.
 *
 * Memoised on first call because canShare with a constructed File is
 * not free (it inspects the platform's supported MIME list).
 */
let _canShareFilesCache = null;
export function canShareFiles() {
  if (_canShareFilesCache !== null) return _canShareFilesCache;
  if (typeof navigator === 'undefined') {
    _canShareFilesCache = false;
    return false;
  }
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') {
    _canShareFilesCache = false;
    return false;
  }
  try {
    const probe = new File([new Uint8Array(1)], 'probe.pdf', {
      type: 'application/pdf',
    });
    _canShareFilesCache = navigator.canShare({ files: [probe] });
  } catch {
    _canShareFilesCache = false;
  }
  return _canShareFilesCache;
}

/**
 * Share a Blob (assumed to be a PDF) via the Web Share API.
 *
 * Result shape — we deliberately don't throw on the common cases so
 * call sites stay flat:
 *   { outcome: 'shared' }                   — share sheet completed
 *   { outcome: 'cancelled' }                — user dismissed (AbortError)
 *   { outcome: 'unsupported' }              — browser can't share files
 *   { outcome: 'error', error: Error }      — anything else
 *
 * Caller is responsible for the fallback when outcome === 'unsupported'
 * (typically: open the PDF in a new tab via the existing blob URL flow).
 */
export async function sharePdfBlob(blob, filename, { title, text } = {}) {
  if (!canShareFiles()) {
    return { outcome: 'unsupported' };
  }

  // Construct a File the share sheet can attach. Some platforms key
  // off the filename's .pdf extension as well as the MIME type, so
  // make sure both line up.
  const file = new File([blob], filename, { type: 'application/pdf' });

  // Last-mile check — `canShare({ files })` may differ for the actual
  // file vs. the probe (e.g. iOS rejects oversized payloads on some
  // models). Better to feature-check than to .share() and fail.
  if (!navigator.canShare({ files: [file] })) {
    return { outcome: 'unsupported' };
  }

  try {
    await navigator.share({
      files: [file],
      ...(title ? { title } : {}),
      ...(text ? { text } : {}),
    });
    return { outcome: 'shared' };
  } catch (err) {
    if (err && err.name === 'AbortError') {
      return { outcome: 'cancelled' };
    }
    return { outcome: 'error', error: err };
  }
}
